import type { SearchClient } from 'algoliasearch/lite';
import type { MultipleQueriesQuery, SearchOptions } from '@algolia/client-search';

type Locale = 'en' | 'fr';
type Environment = 'production';
export type WatchRecencyContext = {
  locale: Locale;
  environment: Environment;
  index: string;
  isBestsellers: boolean;
};
type SortOption = { label: string; value: string };

const INDICES: Record<Environment, Record<Locale, string>> = {
  production: {
    en: 'shopify_products_watch_recency_desc',
    fr: 'shopify_products_canada_fr_watch_recency_desc',
  },
};
const primaryIndex = (locale: Locale) => locale === 'fr'
  ? 'shopify_products_canada_fr' : 'shopify_products';

/**
 * No activation until the server renders readiness for this exact host/locale.
 * The marker must be added only after that index and its data pipeline pass QA.
 * Neither a URL parameter nor an arbitrary index name can activate the feature.
 */
export function watchRecencyForPage(
  document: Pick<Document, 'querySelector'>,
  location: Pick<Location, 'hostname' | 'pathname'>,
  locale: string,
): WatchRecencyContext | null {
  if (locale !== 'en' && locale !== 'fr') return null;
  const environment = ['www.raffi-jewellers.ca', 'raffi-jewellers.ca'].includes(location.hostname) ? 'production' : null;
  if (!environment) return null;
  let pathname: string;
  try { pathname = decodeURIComponent(location.pathname); } catch (_) { return null; }
  if (!/^\/(?:fr\/)?collections\/timepieces(?:\/[a-z0-9-]+)*\/?$/i.test(pathname)
    || /rolex|tag[\s_-]*heuer/i.test(pathname)) return null;
  const main = document.querySelector('main[data-watch-buying="collection"]');
  const index = INDICES[environment][locale];
  if (main?.getAttribute('data-watch-recency-ready') !== 'v1'
    || main.getAttribute('data-watch-recency-index') !== index) return null;
  return { locale, environment, index, isBestsellers: main.getAttribute('data-collection') === 'bestsellers' };
}

/** Run on the original buildSortByOptions output, before Bestsellers mapping. */
export function watchRecencySortOptions<T extends SortOption>(
  options: T[], context: WatchRecencyContext | null, locale: string, newestLabel: string,
): T[] {
  if (!context || (locale !== 'en' && locale !== 'fr')) return options;
  const primary = primaryIndex(locale);
  return options.map((item) => {
    // The original primary-index option is the legacy "What's new" option.
    if (item.value === primary) return { ...item, label: newestLabel, value: INDICES[context.environment][locale] };
    // Keep the first/default option as genuine sales order on Bestsellers.
    if (context.isBestsellers && item.value === `${primary}_featured_asc`) return { ...item, value: primary };
    return item;
  });
}

/** Normalize explicit saved URLs, including locale switches, without changing defaults. */
export function watchRecencyRouteSort(
  route: Record<string, unknown>, context: WatchRecencyContext | null,
): unknown {
  if (!context) return route.sortBy;
  if (!Object.prototype.hasOwnProperty.call(route, 'sortBy')) return undefined;
  const requested = route.sortBy;
  if (typeof requested !== 'string') return undefined;
  if (requested === primaryIndex('en') || requested === primaryIndex('fr')) {
    // Primary is unambiguously the default sales order on Bestsellers. A new
    // newest selection there has its own explicit recency-index value.
    return context.isBestsellers ? primaryIndex(context.locale) : context.index;
  }
  if (Object.values(INDICES).some((pair) => Object.values(pair).includes(requested))) return context.index;
  return requested;
}

const STRICT_RECENCY_PARAMS: SearchOptions = {
  enableRules: false,
  enablePersonalization: false,
  enableReRanking: false,
  enableABTest: false,
  optionalFilters: [],
};

/** Keep filters/pagination/query intact; only the verified recency index opts in. */
export function watchRecencyQueries(
  queries: readonly MultipleQueriesQuery[], context: WatchRecencyContext | null,
): readonly MultipleQueriesQuery[] {
  if (!context) return queries;
  return queries.map((query) => query.indexName === context.index
    ? { ...query, params: { ...query.params, ...STRICT_RECENCY_PARAMS } }
    : query);
}

/** Rules and affinity boosts must never reorder the dedicated chronological index. */
export function withWatchRecencySearch(client: SearchClient, context: WatchRecencyContext | null): SearchClient {
  if (!context) return client;
  return {
    ...client,
    search<TObject>(queries: readonly MultipleQueriesQuery[], requestOptions?: Parameters<SearchClient['search']>[1]) {
      return client.search<TObject>(watchRecencyQueries(queries, context), requestOptions);
    },
  };
}
